export interface LabelUpdate {
  uuid: string;
  name: string;
  color: string;
}

type Listener = (label: LabelUpdate) => void;
const listeners = new Set<Listener>();

export function subscribeLabelUpdates(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function publishLabelUpdate(label: LabelUpdate) {
  listeners.forEach(listener => listener(label));
}

export function updateLabelList<
  T extends { uuid: string; name?: string; color?: string }
>(labels: T[], update: LabelUpdate): T[] {
  if (!Array.isArray(labels)) return labels;
  let changed = false;
  const next = labels.map(label => {
    if (!label || label.uuid !== update.uuid) return label;
    if (label.name === update.name && label.color === update.color)
      return label;
    changed = true;
    return { ...label, name: update.name, color: update.color };
  });
  return changed ? next : labels;
}

export function updateValidationLabels<T>(value: T, update: LabelUpdate): T {
  if (!value || typeof value !== 'object') return value;
  const record = value as any;
  const labels = updateLabelList(record.labels, update);
  let changed = labels !== record.labels;
  const validations = Array.isArray(record.validations)
    ? record.validations.map(row => {
        const next = updateValidationLabels(row, update);
        changed = changed || next !== row;
        return next;
      })
    : record.validations;
  return changed
    ? {
        ...record,
        ...(labels ? { labels } : {}),
        ...(validations ? { validations } : {}),
      }
    : value;
}

// Keep only edits made while this request is in flight, never a persistent cache.
export async function withLabelUpdates<T extends { data: any }>(
  request: () => Promise<T>
): Promise<T> {
  const updates = new Map<string, LabelUpdate>();
  const unsubscribe = subscribeLabelUpdates(label =>
    updates.set(label.uuid, label)
  );
  try {
    const response = await request();
    let data = response.data;
    updates.forEach(label => {
      data = updateValidationLabels(data, label);
    });
    return data === response.data ? response : { ...response, data };
  } finally {
    unsubscribe();
  }
}
